import React, { useState, useEffect } from "react";
import api from "../api";
import Table from "../components/Table";
import Modal from "../components/Modal";
import ImageUpload from "../components/ImageUpload";
import {
  FiPlus,
  FiAward,
  FiUser,
  FiCalendar,
  FiTag,
  FiFileText,
} from "react-icons/fi";
import toast from "react-hot-toast";

const FormGroup = ({ children, cols = 1 }) => {
  const colClasses = {
    1: "grid-cols-1",
    2: "grid-cols-1 sm:grid-cols-2",
    3: "grid-cols-1 sm:grid-cols-3",
  };
  return (
    <div className={`grid ${colClasses[cols]} gap-6 mb-6`}>{children}</div>
  );
};

const InputField = ({
  icon,
  label,
  name,
  type = "text",
  value,
  onChange,
  required = true,
}) => (
  <div>
    <label
      htmlFor={name}
      className="block text-sm font-medium text-gray-700 mb-1"
    >
      {label}
    </label>
    <div className="mt-1 relative rounded-md shadow-sm">
      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
        {icon}
      </div>
      <input
        type={type}
        name={name}
        id={name}
        value={value}
        onChange={onChange}
        className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 sm:text-sm border-gray-300 rounded-md"
        required={required}
      />
    </div>
  </div>
);
const Achievements = () => {
  const [achievements, setAchievements] = useState([]);
  const [colleges, setColleges] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentAchievement, setCurrentAchievement] = useState(null);
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    achieverName: "",
    date: "",
    category: "",
    images: [],
    colleges: [],
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    pages: 0,
  });
  const [searchTerm, setSearchTerm] = useState("");

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    fetchAchievements();
    fetchColleges();
  }, [pagination.page, pagination.limit, searchTerm]);

  const fetchAchievements = async () => {
    try {
      const response = await api.get("/achievement", {
        params: {
          page: pagination.page,
          limit: pagination.limit,
          search: searchTerm,
        },
      });
      setAchievements(response.data.data);
      setPagination((prevState) => ({
        ...prevState,
        total: response.data.pagination.total,
        pages: response.data.pagination.pages,
      }));
    } catch (error) {
      toast.error("Failed to fetch achievements");
    }
  };

  const fetchColleges = async () => {
    try {
      const response = await api.get("/college");
      setColleges(response.data.data);
    } catch (error) {
      toast.error("Failed to fetch colleges");
    }
  };

  const handlePageChange = (newPage) => {
    setPagination((prevState) => ({ ...prevState, page: newPage }));
  };

  const handleLimitChange = (newLimit) => {
    setPagination((prevState) => ({ ...prevState, limit: newLimit, page: 1 }));
  };

  const handleSearch = (term) => {
    setSearchTerm(term);
    setPagination((prevState) => ({ ...prevState, page: 1 }));
  };

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleImageUpload = (urls) => {
    setFormData((prevState) => ({
      ...prevState,
      images: [...prevState.images, ...urls],
    }));
  };

  const handleImageRemove = (index) => {
    setFormData((prevState) => ({
      ...prevState,
      images: prevState.images.filter((_, i) => i !== index),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (currentAchievement) {
        await api.put(`/achievement/${currentAchievement._id}`, formData);
        toast.success("Achievement updated successfully");
      } else {
        await api.post("/achievement", formData);
        toast.success("Achievement created successfully");
      }
      setIsModalOpen(false);
      fetchAchievements();
    } catch (error) {
      toast.error("Failed to save achievement");
    }
  };

  const handleEdit = (achievement) => {
    setCurrentAchievement(achievement);
    setFormData({
      title: achievement.title,
      description: achievement.description,
      achieverName: achievement.achieverName,
      date: achievement.date.split("T")[0],
      category: achievement.category,
      images: achievement.images,
      colleges: achievement.colleges
        ? achievement.colleges.map((c) => c._id)
        : achievement.college
        ? [achievement.college._id]
        : [],
    });
    setIsModalOpen(true);
  };

  const handleCollegeToggle = (collegeId) => {
    setFormData((prevState) => {
      const isSelected = prevState.colleges.includes(collegeId);
      if (isSelected) {
        return {
          ...prevState,
          colleges: prevState.colleges.filter((id) => id !== collegeId),
        };
      } else {
        return { ...prevState, colleges: [...prevState.colleges, collegeId] };
      }
    });
  };

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this achievement?")) {
      try {
        await api.delete(`/achievement/${id}`);
        toast.success("Achievement deleted successfully");
        fetchAchievements();
      } catch (error) {
        toast.error("Failed to delete achievement");
      }
    }
  };

  const columns = [
    { key: "title", label: "Title" },
    { key: "achieverName", label: "Achiever Name" },
    {
      key: "date",
      label: "Date",
      render: (item) => new Date(item.date).toLocaleDateString(),
    },
    { key: "category", label: "Category" },
    {
      key: "college",
      label: "College",
      render: (item) =>
        item.colleges
          ? item.colleges.map((c) => c.name).join(", ")
          : item.college?.name || "N/A",
    },
  ];

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Achievements</h1>
        <button
          onClick={() => {
            setCurrentAchievement(null);
            setFormData({
              title: "",
              description: "",
              achieverName: "",
              date: "",
              category: "",
              images: [],
              colleges: [],
            });
            setIsModalOpen(true);
          }}
          className="px-4 py-2 font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition duration-150 ease-in-out"
        >
          <FiPlus className="inline-block mr-2" />
          Add Achievement
        </button>
      </div>
      <Table
        columns={columns}
        data={achievements}
        onEdit={handleEdit}
        onDelete={handleDelete}
        pagination={pagination}
        onPageChange={handlePageChange}
        onLimitChange={handleLimitChange}
        onSearch={handleSearch}
      />
      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={currentAchievement ? "Edit Achievement" : "Add Achievement"}
        size="xl"
      >
        <form onSubmit={handleSubmit} className="space-y-6">
          <FormGroup cols={2}>
            <InputField
              icon={<FiAward className="h-5 w-5 text-gray-400" />}
              label="Title"
              name="title"
              value={formData.title}
              onChange={handleInputChange}
            />
            <InputField
              icon={<FiUser className="h-5 w-5 text-gray-400" />}
              label="Achiever Name"
              name="achieverName"
              value={formData.achieverName}
              onChange={handleInputChange}
            />
          </FormGroup>
          <FormGroup>
            <div>
              <label
                htmlFor="description"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Description
              </label>
              <div className="mt-1 relative rounded-md shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3 pt-3 flex items-start pointer-events-none">
                  <FiFileText className="h-5 w-5 text-gray-400" />
                </div>
                <textarea
                  name="description"
                  id="description"
                  rows="4"
                  value={formData.description}
                  onChange={handleInputChange}
                  className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 sm:text-sm border-gray-300 rounded-md"
                  required
                ></textarea>
              </div>
            </div>
          </FormGroup>
          <FormGroup cols={3}>
            <InputField
              icon={<FiCalendar className="h-5 w-5 text-gray-400" />}
              label="Date"
              name="date"
              type="date"
              value={formData.date}
              onChange={handleInputChange}
            />
            <InputField
              icon={<FiTag className="h-5 w-5 text-gray-400" />}
              label="Category"
              name="category"
              value={formData.category}
              onChange={handleInputChange}
            />
          </FormGroup>
          <FormGroup>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Colleges
              </label>
              <div className="mt-1 border border-gray-300 rounded-md p-3 max-h-48 overflow-y-auto bg-white">
                {colleges.length === 0 ? (
                  <p className="text-gray-500 text-sm">No colleges available</p>
                ) : (
                  <div className="space-y-2">
                    {colleges.map((college) => (
                      <label
                        key={college._id}
                        className="flex items-center space-x-3 cursor-pointer hover:bg-gray-50 p-1 rounded"
                      >
                        <input
                          type="checkbox"
                          checked={formData.colleges.includes(college._id)}
                          onChange={() => handleCollegeToggle(college._id)}
                          className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                        />
                        <span className="text-sm text-gray-700">
                          {college.name}
                        </span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
              {formData.colleges.length > 0 && (
                <p className="mt-1 text-xs text-gray-500">
                  {formData.colleges.length} college(s) selected
                </p>
              )}
            </div>
          </FormGroup>
          <FormGroup>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Images
              </label>
              <ImageUpload
                onUpload={handleImageUpload}
                onRemove={handleImageRemove}
                images={formData.images}
                multiple
              />
            </div>
          </FormGroup>
          <div className="flex justify-end">
            <button
              type="button"
              onClick={() => setIsModalOpen(false)}
              className="mr-3 px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              {currentAchievement ? "Update" : "Create"}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default Achievements;
